import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkSmAccountIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/account';
import BpkSmAirconIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/aircon';
import BpkSmAirportsIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/airports';
import BpkSmBaggageGenericIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/baggage-generic';
import BpkSmBusIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/bus';
import BpkSmCarsFlexibleIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/cars-flexible';
import BpkSmElectricIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/electric';
import BpkSmGearsAutomaticIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/gears-automatic';
import BpkSmGearsManualIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/gears-manual';
import BpkSmMileageIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/mileage';
import BpkSmPaidIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/paid';
import BpkSmSelfServiceIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/self-service';
import {
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  ThemeCardV2Props,
  Badge,
  IconKey,
  Policy,
  Price,
  Vendor,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardV2Props';

import STYLES from './ThemeCardV2.module.scss';

const Icons: Record<IconKey, JSX.Element> = {
  account: <BpkSmAccountIcon />,
  aircon: <BpkSmAirconIcon />,
  airports: <BpkSmAirportsIcon />,
  baggage: <BpkSmBaggageGenericIcon />,
  bus: <BpkSmBusIcon />,
  'cars-flexible': <BpkSmCarsFlexibleIcon />,
  electric: <BpkSmElectricIcon />,
  'gears-automatic': <BpkSmGearsAutomaticIcon />,
  'gears-manual': <BpkSmGearsManualIcon />,
  mileage: <BpkSmMileageIcon />,
  paid: <BpkSmPaidIcon />,
  'self-service': <BpkSmSelfServiceIcon />,
};

const Header = ({
  subtitle,
  title,
}: Pick<ThemeCardV2Props, 'title' | 'subtitle'>) => (
  <BpkFlex direction="column">
    <BpkText
      tagName="h3"
      textStyle={TEXT_STYLES.heading4}
      color={TEXT_COLORS.textPrimary}
    >
      {title}
    </BpkText>
    {subtitle && (
      <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
        {subtitle}
      </BpkText>
    )}
  </BpkFlex>
);

const Features = ({
  badges = undefined,
  vendor = undefined,
}: {
  vendor?: Vendor;
  badges?: Badge[];
}) => (
  <BpkFlex
    direction="column"
    grow={1}
    basis="0%"
    gap={BpkSpacing.SM}
    minWidth="0rem"
  >
    {vendor && (
      // Cannot use BpkBox/BpkVessel: both forbid `as="img"` (verified
      // against BpkVessel's VesselElement type, which lists div/span/
      // article/section/nav/main/aside/header/footer but not img), so a
      // raw <img> with layout/visual styling has no Backpack primitive to
      // move to at all. See
      // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
      <img
        className={STYLES.vendorLogo}
        src={vendor.logoSrc}
        alt={vendor.logoAlt}
      />
    )}
    {/* BpkVessel (not BpkBox/BpkFlex): needs `align-content`, which neither
        BpkBox nor BpkFlex expose as a prop (only `align`/`align-items`,
        which controls single-line cross-axis alignment, not multi-line
        wrap distribution). See
        backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
    <BpkVessel as="div" className={STYLES.badges}>
      {badges?.map((badge: Badge) => (
        <BpkBadge
          key={badge.title}
          type={BADGE_TYPES.normal}
          className={STYLES.badge}
          aria-label={badge.ariaLabel}
          role="status"
        >
          {badge.icon && (Icons[badge.icon] || null)}
          {badge.title}
        </BpkBadge>
      ))}
    </BpkVessel>
  </BpkFlex>
);

const PriceSection = ({
  policies = undefined,
  price = undefined,
}: {
  policies?: Policy[];
  price?: Price;
}) => (
  // BpkVessel (not BpkBox/BpkFlex): needs `border-top`, which BpkBox/BpkFlex
  // don't expose (`border`/`borderTop` are typed `never`). See
  // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
  <BpkVessel as="div" className={STYLES.priceSection}>
    {policies && policies.length > 0 && (
      <BpkFlex direction="column" gap={BpkSpacing.SM}>
        {policies.map((policy: Policy) => (
          <BpkFlex key={policy.text} gap={BpkSpacing.MD} align="center">
            {policy.icon && (Icons[policy.icon] || null) && (
              <BpkFlex as="span" align="center" shrink={0}>
                {Icons[policy.icon]}
              </BpkFlex>
            )}
            <BpkText
              tagName="p"
              textStyle={TEXT_STYLES.footnote}
              color={TEXT_COLORS.textPrimary}
            >
              {policy.text}
            </BpkText>
          </BpkFlex>
        ))}
      </BpkFlex>
    )}
    {price?.value && (
      <BpkPrice
        className={STYLES.price}
        price={price.value}
        size={SIZES.large}
        align={ALIGNS.right}
        leadingText={price.leadingText}
        trailingText={price.trailingText}
      />
    )}
  </BpkVessel>
);

const ThemeCardV2 = ({
  badges,
  href,
  image,
  index,
  policies,
  price,
  subtitle,
  title,
  vendor,
}: ThemeCardV2Props) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdName = `${rawComponentName}_item`;

  return (
    // BpkVessel (not BpkBox/BpkFlex): `.ThemeCardV2` (on the BpkCard below)
    // is nested under `.ThemeCardV2Wrapper` without `&` in the SCSS,
    // compiling to the compound selector `.ThemeCardV2Wrapper .ThemeCardV2`
    // that requires this className to remain in the DOM as an ancestor.
    // `.ThemeCardV2` itself can't drop its className anyway: `$card-margin:
    // bpk-spacing-base() * 0.75` = 0.75rem, and BpkMarginValue only accepts
    // spacing tokens/percentages/auto, not arbitrary rem (verified against
    // tokens.js isValidMarginValue — no token matches 0.75rem; same gap as
    // ThemeCard/InventoryCard's wrapper chain). See
    // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
    <BpkVessel
      as="div"
      className={STYLES.ThemeCardV2Wrapper}
      {...(index !== undefined && {
        'data-tracking-element-id': `${mbdName}_${index}`,
        'data-tracking-common-params': buildMBDParam(mbdName),
      })}
    >
      <BpkCard padded={false} className={STYLES.ThemeCardV2} href={href}>
        <BpkFlex
          direction="column"
          grow={1}
          basis="0%"
          gap={BpkSpacing.Base}
          padding={BpkSpacing.Base}
        >
          <Header title={title} subtitle={subtitle} />
          <BpkFlex
            grow={1}
            basis="0%"
            gap={BpkSpacing.Base}
            align="stretch"
            overflow="hidden"
          >
            <Features vendor={vendor} badges={badges} />
            {/* Cannot use BpkBox/BpkVessel: both forbid `as="img"`, so a
                raw <img> with layout/visual styling has no Backpack
                primitive to move to at all. See
                backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
            <img className={STYLES.image} src={image.src} alt={image.altText} />
          </BpkFlex>
        </BpkFlex>
        <PriceSection policies={policies} price={price} />
      </BpkCard>
    </BpkVessel>
  );
};

export default ThemeCardV2;
